import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { PortalDsField, PortalDsInput } from 'src/components/portal-design-system';
import { useGetPlatformHealthQuery } from 'src/redux/operations/operations.endpoints';
import { mergePlatformHealthSummaries } from '../helpers/platform-health.helpers';
import { PlatformHealthGrid } from './platform-health-grid';

export function PlatformHealthSection() {
  const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);
  const { data, isLoading, isFetching, isError, error, refetch } = useGetPlatformHealthQuery(
    selectedDate ? { date: selectedDate } : {}
  );

  useEffect(() => {
    if (!selectedDate && data?.check_date) {
      setSelectedDate(data.check_date);
    }
  }, [data?.check_date, selectedDate]);

  const rows = useMemo(
    () => mergePlatformHealthSummaries(data?.summaries ?? [], selectedDate),
    [data?.summaries, selectedDate]
  );

  const hasNoData = !isLoading && !isError && (data?.available_dates?.length ?? 0) === 0;

  return (
    <section
      className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5"
      aria-labelledby="platform-health-heading"
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2
            id="platform-health-heading"
            className="text-base font-semibold text-gray-900"
          >
            Platform health
          </h2>
          <p className="text-sm text-gray-600">
            Data quality verification status across platforms — do I need to investigate?
          </p>
        </div>
        <button
          type="button"
          className="text-sm font-medium text-violet-700 hover:text-violet-900 disabled:opacity-50"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          Refresh
        </button>
      </div>

      {!hasNoData && (
        <div className="mb-4 max-w-xs">
          <PortalDsField label="Check date" htmlFor="platform-health-check-date" filter>
            <PortalDsInput
              id="platform-health-check-date"
              type="date"
              value={selectedDate ?? ''}
              onChange={(event) => setSelectedDate(event.target.value)}
              disabled={!data?.available_dates?.length}
            />
          </PortalDsField>
        </div>
      )}

      {(isLoading || isFetching) && (
        <div
          className="flex items-center gap-2 text-sm text-gray-600"
          role="status"
        >
          <Loader2 className="size-4 animate-spin" />
          Loading platform health…
        </div>
      )}

      {isError && !isLoading && (
        <div
          className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
          role="alert"
        >
          <p className="font-medium">
            Could not load platform health
          </p>
          <p className="mt-1 text-red-700">
            {(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
          </p>
        </div>
      )}

      {hasNoData && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
          <p className="text-sm text-gray-600">
            No verification data available. Verification flow not yet running.
          </p>
        </div>
      )}

      {!isLoading && !isError && !hasNoData && rows.length === 0 && (
        <p className="text-sm text-gray-600">
          No platform health data for
          {' '}
          {selectedDate ?? 'the selected date'}
          .
        </p>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <PlatformHealthGrid rows={rows} />
      )}
    </section>
  );
}
